export default {
    namespaced: true,

    state: {
        tasks: [],
        selectedTask: null,
        statuses: [],
        priorities: [],
        statusesLoaded: false,
        prioritiesLoaded: false,
        statusesLoading: false,
        prioritiesLoading: false,
        filters: {
            status_id: null,
            priority_id: null,
            assignee_type: null,
            assignee_id: null,
            project: null,
            site: null,
            search: ''
        },
        stats: {
            total: 0,
            by_status: {},
            by_priority: {},
            overdue: 0,
            due_this_week: 0
        },
        pagination: {
            current_page: 1,
            per_page: 25,
            total: 0,
            last_page: 1
        },
        loading: false,
        error: null
    },

    mutations: {
        SET_TASKS(state, tasks) {
            state.tasks = tasks;
        },

        SET_SELECTED_TASK(state, task) {
            state.selectedTask = task;
        },

        SET_STATUSES(state, statuses) {
            state.statuses = statuses;
        },

        SET_PRIORITIES(state, priorities) {
            state.priorities = priorities;
        },

        SET_STATUSES_LOADED(state, val) { state.statusesLoaded = val; },
        SET_PRIORITIES_LOADED(state, val) { state.prioritiesLoaded = val; },
        SET_STATUSES_LOADING(state, val) { state.statusesLoading = val; },
        SET_PRIORITIES_LOADING(state, val) { state.prioritiesLoading = val; },

        SET_FILTERS(state, filters) {
            state.filters = { ...state.filters, ...filters };
        },

        SET_STATS(state, stats) {
            state.stats = stats;
        },

        SET_PAGINATION(state, pagination) {
            state.pagination = pagination;
        },

        SET_LOADING(state, loading) {
            state.loading = loading;
        },

        SET_ERROR(state, error) {
            state.error = error;
        },

        ADD_TASK(state, task) {
            state.tasks.unshift(task);
        },

        UPDATE_TASK(state, updatedTask) {
            const index = state.tasks.findIndex(t => t.id === updatedTask.id);
            if (index !== -1) {
                state.tasks.splice(index, 1, updatedTask);
            }
        },

        REMOVE_TASK(state, taskId) {
            state.tasks = state.tasks.filter(t => t.id !== taskId);
        }
    },

    actions: {
        async fetchStatuses({ commit, state }) {
            if (state.statusesLoaded || state.statusesLoading) return;
            commit('SET_STATUSES_LOADING', true);
            try {
                const response = await axios.get('/api/task-statuses');
                commit('SET_STATUSES', response.data.data);
                commit('SET_STATUSES_LOADED', true);
            } catch (error) {
                console.error('Error fetching task statuses:', error);
                commit('SET_ERROR', error.message);
            } finally {
                commit('SET_STATUSES_LOADING', false);
            }
        },

        async fetchPriorities({ commit, state }) {
            if (state.prioritiesLoaded || state.prioritiesLoading) return;
            commit('SET_PRIORITIES_LOADING', true);
            try {
                const response = await axios.get('/api/task-priorities');
                commit('SET_PRIORITIES', response.data.data);
                commit('SET_PRIORITIES_LOADED', true);
            } catch (error) {
                console.error('Error fetching task priorities:', error);
                commit('SET_ERROR', error.message);
            } finally {
                commit('SET_PRIORITIES_LOADING', false);
            }
        },

        async fetchTasks({ commit, state }, params = {}) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);

            try {
                const queryParams = {
                    page: state.pagination?.current_page || 1,
                    per_page: state.pagination?.per_page || 15,
                    ...state.filters,
                    ...params
                };

                const response = await axios.get('/api/tasks', { params: queryParams });

                // Handle both paginated and non-paginated responses
                if (response.data.data) {
                    commit('SET_TASKS', response.data.data);
                    if (response.data.meta) {
                        commit('SET_PAGINATION', response.data.meta);
                    }
                } else {
                    commit('SET_TASKS', response.data);
                }
            } catch (error) {
                console.error('Error fetching tasks:', error);
                commit('SET_ERROR', error.message);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async fetchStats({ commit }) {
            try {
                const response = await axios.get('/api/tasks/stats');
                commit('SET_STATS', response.data);
            } catch (error) {
                console.error('Error fetching task stats:', error);
                commit('SET_ERROR', error.message);
            }
        },

        async createTask({ commit }, taskData) {
            try {
                const response = await axios.post('/api/tasks', taskData);
                commit('ADD_TASK', response.data);
                return response.data;
            } catch (error) {
                console.error('Error creating task:', error);
                commit('SET_ERROR', error.message);
                throw error;
            }
        },

        async updateTask({ commit }, { id, data }) {
            try {
                const response = await axios.put(`/api/tasks/${id}`, data);
                commit('UPDATE_TASK', response.data);
                return response.data;
            } catch (error) {
                console.error('Error updating task:', error);
                commit('SET_ERROR', error.message);
                throw error;
            }
        },

        async updateTaskStatus({ commit }, { id, status_id }) {
            try {
                const response = await axios.patch(`/api/tasks/${id}/status`, { status_id });
                commit('UPDATE_TASK', response.data);
                return response.data;
            } catch (error) {
                console.error('Error updating task status:', error);
                commit('SET_ERROR', error.message);
                throw error;
            }
        },

        async deleteTask({ commit }, taskId) {
            try {
                await axios.delete(`/api/tasks/${taskId}`);
                commit('REMOVE_TASK', taskId);
            } catch (error) {
                console.error('Error deleting task:', error);
                commit('SET_ERROR', error.message);
                throw error;
            }
        }
    },

    getters: {
        tasksByStatus: (state) => (statusId) => {
            return state.tasks.filter(t => t.status_id === statusId);
        },

        overdueTasks: (state) => {
            return state.tasks.filter(t => t.is_overdue);
        },

        tasksForCalendar: (state) => {
            return state.tasks.filter(t => t.due_date !== null);
        },

        activeStatuses: (state) => {
            return state.statuses
                .filter(s => s.is_active)
                .sort((a, b) => a.order_index - b.order_index);
        },

        activePriorities: (state) => {
            return state.priorities
                .filter(p => p.is_active)
                .sort((a, b) => a.order_index - b.order_index);
        },

        tasksByPriority: (state) => (priorityId) => {
            if (priorityId === null) {
                return state.tasks.filter(t => !t.priority_id);
            }
            return state.tasks.filter(t => t.priority_id === priorityId);
        }
    }
};
